import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { TrackingCategory, trackingService, TrackingTranscriptionParams } from '../../../services';
import type { ITheme } from '../../../theme';
import { YoutubePreview } from '../../molecules';

const useStyles = makeStyles()((theme: ITheme) => ({
  youtubeInfo: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    gridTemplateRows: '1fr 1fr',
    gap: theme.spacing(),
  },
}));

export interface YoutubeTranscriptionDialogProps {
  url: string;
  onConfirm: () => void;
  onCancel: () => void;
  open: boolean;
}

const YoutubeTranscriptionDialog: React.FC<YoutubeTranscriptionDialogProps> = ({
  url,
  onConfirm,
  onCancel,
  open,
}: YoutubeTranscriptionDialogProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const defaultVideoInfo = useMemo(
    () => ({
      title: t('home.youtube.unknown_title'),
      duration: t('home.youtube.unknown_duration'),
    }),
    [t],
  );

  const [valid, setValid] = useState(false);
  const [tooLong, setTooLong] = useState(false);
  const [loading, setLoading] = useState(true);
  const [player, setPlayer] = useState<any>(null);

  const [videoInfo, setVideoInfo] = useState<{ title: string; duration: string }>(defaultVideoInfo);
  useEffect(() => {
    setTooLong(false);
  }, [url]);

  // extract some info to display from player
  useEffect(() => {
    if (player) {
      const { videoTitle } = player;
      const duration: number = player.getDuration() as number;

      setVideoInfo({
        title: videoTitle,
        duration: `${new Date(duration * 1000).toISOString().substring(14, 19)} min`,
      });
    } else {
      setVideoInfo(defaultVideoInfo);
    }
  }, [player]);

  const handleConfirm = async () => {
    const trackingValues = {
      input: TrackingTranscriptionParams.input.youtube,
      location: TrackingTranscriptionParams.location.songbook,
    };
    trackingService.valueObjectEvent(
      TrackingCategory.Transcription,
      TrackingTranscriptionParams.action.start,
      {
        ...trackingValues,
      },
    );
    // console.log(TrackingTranscriptionParams.action.start, { ...trackingValues });
    onConfirm();
  };

  return (
    <Dialog open={open}>
      <DialogTitle>{t('home.youtube.dialog_title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('home.youtube.explanation')}

          <div className={classes.youtubeInfo}>
            <Typography component="span" variant="subtitle2">
              {t('home.youtube.title')}
            </Typography>
            <Typography component="span" variant="body1">
              {videoInfo.title}
            </Typography>

            <Typography component="span" variant="subtitle2">
              {t('home.youtube.length')}
            </Typography>
            <Typography component="span" variant="body1">
              {videoInfo.duration}
            </Typography>
          </div>
        </DialogContentText>

        <YoutubePreview
          youtubeUrl={url}
          onError={() => {
            setValid(false);
            setLoading(false);
          }}
          onDurationTooLong={(e) => {
            setValid(false);
            setTooLong(true);
            setLoading(false);
            if (e.target) {
              setPlayer(e.target);
            }
          }}
          onReady={(e) => {
            if (e.target) {
              setPlayer(e.target);
            }
            setValid(true);
            setLoading(false);
          }}
        />

        {!loading && tooLong && <Alert severity="error">{t('home.input.duration_too_long')}</Alert>}
        {!loading && !valid && <Alert severity="error">{t('home.input.url_not_valid')}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button id="btn_youtube_dialog_cancel" onClick={onCancel}>
          {t('home.dialog.cancel')}
        </Button>
        <Button
          id="btn_youtube_dialog_submit"
          onClick={handleConfirm}
          disabled={!valid}
          type="submit"
        >
          {t('home.dialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default YoutubeTranscriptionDialog;
